const MS_PER_DAY = 24 * 60 * 60 * 1000;

export const DECISION_STATUS_FILTER_VALUES = ['all', 'open', 'prepared', 'decided', 'escalated'];

export const DECISION_STATUS_STYLES = {
  open: {
    backgroundColor: '#FEF2F2',
    color: '#EF4444',
    labelKey: 'open',
  },
  prepared: {
    backgroundColor: '#FEFCE8',
    color: '#EAB308',
    labelKey: 'prepared',
  },
  decided: {
    backgroundColor: '#F0FDF4',
    color: '#22C55E',
    labelKey: 'decided',
  },
  escalated: {
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    labelKey: 'escalated',
  },
};

export const ESCALATION_TIER_STYLES = {
  none: { color: 'var(--color-disabled)', labelKey: 'none' },
  medium: { color: 'var(--color-warning-text)', labelKey: 'medium' },
  high: { color: 'var(--color-error-text)', labelKey: 'high' },
};

export function isPendingDecisionStatus(status) {
  return status === 'open' || status === 'prepared' || status === 'escalated';
}

export function escalationTierFromLevel(level) {
  const value = Number(level) || 0;
  if (value >= 2) return 'high';
  if (value >= 1) return 'medium';
  return 'none';
}

export function normalizeDecisionRow(item, projectMeta = {}) {
  return {
    ...item,
    projectId: projectMeta.projectId ?? item.projectId,
    projectName: projectMeta.projectName ?? item.projectName ?? '',
  };
}

export function filterDecisionsByStatus(items, statusFilter) {
  if (!statusFilter || statusFilter === 'all') return items;
  return items.filter((item) => item.status === statusFilter);
}

function matchesDueFilter(item, dueFilter) {
  if (!dueFilter) return true;
  if (!item.dueDate) return dueFilter === 'noDueDate';
  const due = new Date(item.dueDate);
  if (Number.isNaN(due.getTime())) return true;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);

  if (dueFilter === 'overdue') {
    return Boolean(item.isOverdue) && item.status !== 'decided';
  }
  if (dueFilter === 'thisWeek') {
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + (7 - weekEnd.getDay()));
    return due >= today && due <= weekEnd;
  }
  if (dueFilter === 'next30Days') {
    const limit = new Date(today);
    limit.setDate(limit.getDate() + 30);
    return due >= today && due <= limit;
  }
  return true;
}

function matchesEscalationFilter(item, escalationFilter) {
  if (!escalationFilter) return true;
  const tier = escalationTierFromLevel(item.escalationLevel);
  if (escalationFilter === 'high') return tier === 'high';
  if (escalationFilter === 'medium') return tier === 'medium';
  if (escalationFilter === 'none') return tier === 'none';
  return true;
}

export function filterDecisions(items, filters = {}) {
  const {
    statusFilter = 'all',
    deciderFilter = '',
    projectFilter = '',
    milestoneFilter = '',
    dueFilter = '',
    escalationFilter = '',
  } = filters;

  return items.filter((item) => {
    if (!filterDecisionsByStatus([item], statusFilter).length) return false;
    if (deciderFilter && String(item.decisionOwnerUserId) !== String(deciderFilter)) return false;
    if (projectFilter && String(item.projectId) !== String(projectFilter)) return false;
    if (milestoneFilter && String(item.milestoneId) !== String(milestoneFilter)) return false;
    if (!matchesDueFilter(item, dueFilter)) return false;
    if (!matchesEscalationFilter(item, escalationFilter)) return false;
    return true;
  });
}

export function sortDecisionsForList(items) {
  const statusOrder = { escalated: 0, open: 1, prepared: 2, decided: 3 };
  return [...items].sort((a, b) => {
    const statusDiff = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
    if (statusDiff !== 0) return statusDiff;
    if (Boolean(a.isOverdue) !== Boolean(b.isOverdue)) return a.isOverdue ? -1 : 1;
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });
}

function averageDecisionDurationDays(items) {
  const durations = items
    .filter((item) => item.status === 'decided' && item.decidedAt && item.date_created)
    .map((item) => {
      const start = new Date(item.date_created).getTime();
      const end = new Date(item.decidedAt).getTime();
      return Math.max(0, Math.round((end - start) / MS_PER_DAY));
    });
  if (durations.length === 0) return null;
  return Math.round(durations.reduce((sum, days) => sum + days, 0) / durations.length);
}

export function computeDecisionKpis(items) {
  const pending = items.filter((item) => isPendingDecisionStatus(item.status));
  const open = items.filter((item) => item.status === 'open');
  const prepared = items.filter((item) => item.status === 'prepared');
  const overdue = pending.filter((item) => item.isOverdue);
  const escalated = pending.filter((item) => item.status === 'escalated');

  return {
    avgDurationDays: averageDecisionDurationDays(items),
    overdueCount: overdue.length,
    pendingCount: pending.length,
    escalatedCount: escalated.length,
    escalatedPercent: pending.length > 0
      ? Math.round((escalated.length / pending.length) * 100)
      : 0,
    openCount: open.length,
    preparedCount: prepared.length,
  };
}

export function buildDeciderFilterOptions(items, users = []) {
  const ids = [...new Set(items.map((item) => item.decisionOwnerUserId).filter(Boolean))];
  return ids.map((id) => {
    const user = users.find((entry) => String(entry.id) === String(id));
    return {
      value: String(id),
      label: user?.name || user?.email || String(id),
    };
  }).sort((a, b) => a.label.localeCompare(b.label));
}

export function buildProjectFilterOptions(items) {
  const byProject = new Map();
  items.forEach((item) => {
    if (!item.projectId) return;
    byProject.set(String(item.projectId), item.projectName || item.projectId);
  });
  return [...byProject.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function buildMilestoneFilterOptions(items) {
  const byMilestone = new Map();
  items.forEach((item) => {
    if (!item.milestoneId) return;
    byMilestone.set(String(item.milestoneId), item.milestoneTitle || item.milestoneId);
  });
  return [...byMilestone.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function formatDecisionDate(value, locale) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(locale === 'de' ? 'de-DE' : 'en-GB');
}

export function daysBetween(from, to = new Date()) {
  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return Math.round((end.getTime() - start.getTime()) / MS_PER_DAY);
}

export function formatRelativeDaysAgo(fromDate, locale = 'de', to = new Date()) {
  const days = daysBetween(fromDate, to);
  if (days === null) return '—';
  if (days <= 0) return locale === 'de' ? 'heute' : 'today';
  if (locale === 'de') return `vor ${days} ${days === 1 ? 'Tag' : 'Tagen'}`;
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

export function formatDaysCount(days, locale = 'de') {
  if (days === null || days === undefined) return '—';
  const value = Math.abs(days);
  if (locale === 'de') return `${value} ${value === 1 ? 'Tag' : 'Tagen'}`;
  return `${value} ${value === 1 ? 'day' : 'days'}`;
}

function hasReachedStatus(status, target) {
  const order = { open: 0, prepared: 1, escalated: 2, decided: 3 };
  return (order[status] ?? 0) >= (order[target] ?? 0);
}

function findHistoryAt(decision, event) {
  const history = Array.isArray(decision?.statusHistory) ? decision.statusHistory : [];
  const matches = history.filter((entry) => entry?.event === event && entry?.at);
  if (!matches.length) return null;
  return matches[matches.length - 1].at;
}

export function buildDecisionTimelineSteps(decision, locale = 'de') {
  if (!decision) return [];

  const createdAt = findHistoryAt(decision, 'created') || decision.date_created;
  const preparedAt = findHistoryAt(decision, 'prepared');
  const escalatedAt = findHistoryAt(decision, 'escalated');
  const updatedAt = decision.date_updated || createdAt;

  const isPrepared = Boolean(preparedAt)
    || (hasReachedStatus(decision.status, 'prepared') && decision.status !== 'open');
  const isEscalated = Boolean(escalatedAt) || decision.status === 'escalated';

  const preparedStamp = preparedAt || (isPrepared ? updatedAt : null);
  const escalatedStamp = escalatedAt || (isEscalated ? updatedAt : null);

  return [
    {
      key: 'created',
      value: formatDecisionDate(createdAt, locale),
      detail: formatRelativeDaysAgo(createdAt, locale),
      detailTone: 'muted',
    },
    {
      key: 'prepared',
      value: preparedStamp ? formatDecisionDate(preparedStamp, locale) : '—',
      detail: preparedStamp ? formatRelativeDaysAgo(preparedStamp, locale) : '—',
      detailTone: 'muted',
    },
    {
      key: 'escalated',
      value: escalatedStamp ? formatDecisionDate(escalatedStamp, locale) : '—',
      detail: isEscalated
        ? (locale === 'de' ? 'Eskaliert' : 'Escalated')
        : '—',
      detailTone: isEscalated ? 'error' : 'muted',
    },
  ];
}

export function buildDecisionTimingMeta(decision, locale = 'de', now = new Date()) {
  if (!decision) {
    return {
      daysSinceCreation: null,
      dueTimingDays: null,
      isOverdue: false,
      showEscalationHighlight: false,
    };
  }

  const daysSinceCreation = daysBetween(decision.date_created, now);
  let dueTimingDays = null;
  if (decision.dueDate) {
    dueTimingDays = daysBetween(now, decision.dueDate);
  }

  return {
    daysSinceCreation,
    dueTimingDays,
    isOverdue: Boolean(decision.isOverdue),
    showEscalationHighlight: decision.status === 'escalated',
  };
}
